import { apiErrorMessage } from '@agos/api-client'
import {
  getGetSettingsFranchiseQueryKey,
  useGetSettingsFranchise,
  usePutSettingsFranchise,
} from '@agos/api-client/platform'
import { T, formatCount, formatPercent, tt, useAuth } from '@agos/shared'
import { PageHeader, Spinner, cn, useToast, BUTTON_PRIMARY} from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  BarChart3,
  CreditCard,
  Info,
  Percent,
  Phone,
  Receipt,
  RefreshCw,
  Save,
  Store,
} from 'lucide-react'
import { useState } from 'react'

import { mayWrite, readOnlyReason } from '../write-access'
import {
  MONEY_FIELDS,
  PLATFORM_REVENUE_SHARE_PCT,
  READ_BY_NOTHING,
  feeDisagreesWithPlatform,
  fieldState,
  formOf,
  methodLabel,
  numberProblem,
  numberProblemMessage,
  payloadOf,
  problemFields,
  storedKeys,
  suggestionFor,
  suggestionLabel,
  type BillingMethod,
  type FranchiseForm,
  type FranchiseSettings,
  type NumericField,
} from './franchise-settings'

/**
 * `/settings-franchise` — nineteen fields, one stored key, and nothing reads any of them.
 *
 * **FB-137.** `GET /settings/franchise` answers `{"franchiseName": ""}` on the restored data. Legacy then
 * spreads nineteen literals underneath it, so *Výška poplatku pre franchise v percentách* shows **5** — a
 * number in a React file, five times the **1,00 %** the platform's `BillingRunner` actually bills from
 * `platform_billing_settings.revenue_share_pct`. Editing it changes nothing: two files in the whole legacy
 * codebase mention these keys, the page and the zod schema on its own write path.
 *
 * Three of the four selects offer one option each, and all four store `tt()`-translated display strings, so
 * what lands in the database depends on the language of the browser that last saved.
 *
 * The port keeps every field — a franchise's record of its own terms is the owner's to delete, not a
 * porting decision — but a literal no longer passes for an agreement. An unstored field is empty and says
 * so, with the legacy default offered on a button; the selects store stable keys; and the page states, once
 * and plainly, that nothing here changes what the central office bills.
 *
 * Ported from legacy `pages/settings-franchise.tsx` (267 lines).
 */

export function SettingsFranchisePage() {
  const { data, isLoading } = useGetSettingsFranchise()

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    )
  }

  // `GET /settings/franchise` answers `null` for a franchise with no row at all — two of the three in the
  // restored data have none.
  return <FranchiseEditor settings={data ?? undefined} />
}

const FIELD =
  'px-3 py-1.5 rounded-lg bg-background border border-border text-sm outline-none focus:ring-2 focus:ring-primary/20 w-56 disabled:opacity-60'

function FranchiseEditor({ settings }: { settings: FranchiseSettings | undefined }) {
  const { toast } = useToast()
  const { user } = useAuth()
  const queryClient = useQueryClient()

  const [form, setForm] = useState<FranchiseForm>(() => formOf(settings))
  const writable = mayWrite('franchise', user?.systemRole)
  const readOnly = readOnlyReason('franchise', user?.systemRole)

  const stored = storedKeys(settings)
  const problems = problemFields(form)

  const save = usePutSettingsFranchise({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: getGetSettingsFranchiseQueryKey() })
        toast({ title: tt('Nastavenia franšízy uložené') })
      },
      // Legacy throws on `!r.ok` and only `console.error`s, so a refusal looks exactly like never
      // having clicked.
      onError: (caught) =>
        toast({
          variant: 'destructive',
          title: tt('Uloženie zlyhalo'),
          description: apiErrorMessage(caught, tt('Na serveri sa nič nezmenilo.')),
        }),
    },
  })

  const set = <K extends keyof FranchiseForm>(key: K, value: FranchiseForm[K]) =>
    setForm((current) => ({ ...current, [key]: value }))

  return (
    <div className="space-y-5">
      <PageHeader
        title={tt('Nastavenia franšízy')}
        description={tt('Záznam o podmienkach tejto prevádzky - fakturáciu určuje centrála')}
        icon={<Store className="w-5 h-5" />}
      />

      {readOnly !== null && (
        <p className="text-[11px] text-amber-500" data-read-only>
          {readOnly}
        </p>
      )}

      {/* FB-137. The page's whole premise, said once. */}
      <div
        className="rounded-2xl border border-amber-500/40 bg-amber-500/5 px-4 py-3 flex items-start gap-3"
        data-nothing-reads-this
      >
        <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
        <div className="min-w-0 space-y-1">
          <p className="text-sm font-semibold text-amber-500">
            <T>Nič na tejto stránke neovplyvňuje to, čo centrála fakturuje</T>
          </p>
          <p className="text-xs text-muted-foreground">
            {formatCount(READ_BY_NOTHING.length)}{' '}
            <T>
              z týchto polí nečíta žiadny report, faktúra ani úloha - sú to poznámky. Skutočné podmienky sú
              v centrále a na tejto obrazovke sa nedajú zmeniť.
            </T>
          </p>
          <p className="text-xs text-muted-foreground" data-stored-count>
            {stored.length === 0 ? (
              <T>Zo sekcie nie je uložené ani jedno pole - všetko, čo vidíte prázdne, nikto nenastavil.</T>
            ) : (
              <>
                <T>Uložené polia</T>: {stored.join(', ')}
              </>
            )}
          </p>
        </div>
      </div>

      {feeDisagreesWithPlatform(form) && (
        <div
          className="rounded-xl border border-red-500/40 bg-red-500/5 px-4 py-3 text-xs"
          data-fee-disagrees
        >
          <p className="font-bold text-red-500 flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5" />
            <T>Toto percento nie je to, čo sa fakturuje</T>
          </p>
          <p className="text-muted-foreground mt-0.5">
            <T>Centrála účtuje</T> {formatPercent(PLATFORM_REVENUE_SHARE_PCT)} <T>z tržieb</T>.{' '}
            <T>Číslo tu je len poznámka a nezmení výpočet.</T>
          </p>
        </div>
      )}

      <Section title={tt('Identifikácia')} icon={<Store className="w-4 h-4 text-primary" />}>
        <TextRow
          field="appName"
          label={tt('Názov aplikácie')}
          desc={tt('Poznámka. Názov v odchádzajúcich e-mailoch nastavuje server, nie toto pole.')}
          form={form}
          settings={settings}
          disabled={!writable}
          onChange={set}
        />
        <TextRow
          field="franchiseId"
          label={tt('ID franšízy')}
          desc={tt('Poznámka. Franšízu nesmeruje toto pole, ale registrácia v centrále.')}
          placeholder="FK-001"
          form={form}
          settings={settings}
          disabled={!writable}
          onChange={set}
        />
        <TextRow
          field="franchiseName"
          label={tt('Názov franšízovej siete')}
          desc={tt('Jediné pole tejto sekcie, ktoré niečo drží - a centrála má vlastný, nadradený názov.')}
          placeholder="PizzaMajstro Slovakia"
          form={form}
          settings={settings}
          disabled={!writable}
          onChange={set}
        />
      </Section>

      <Section title={tt('Fakturácia')} icon={<Receipt className="w-4 h-4 text-primary" />}>
        <Toggle
          label={tt('Povoliť fakturáciu')}
          desc={tt('Poznámka - fakturáciu spúšťa centrála.')}
          value={form.billingEnabled}
          disabled={!writable}
          onToggle={() => set('billingEnabled', !form.billingEnabled)}
          testId="billingEnabled"
        />
        <NumberRow
          field="billingCoefficient"
          label={tt('Fakturačný koeficient')}
          form={form}
          settings={settings}
          disabled={!writable}
          onChange={set}
        />
        <NumberRow
          field="smsPrice"
          label={tt('Cena za SMS')}
          desc={tt('V cenníku centrály nie je žiadna položka za SMS.')}
          suffix="€"
          form={form}
          settings={settings}
          disabled={!writable}
          onChange={set}
        />
        <NumberRow
          field="fixedBillingPrice"
          label={tt('Fixná cena fakturácie')}
          suffix="€"
          form={form}
          settings={settings}
          disabled={!writable}
          onChange={set}
        />
      </Section>

      <Section
        title={tt('Franchise a provízne partnerstvo')}
        icon={<Percent className="w-4 h-4 text-primary" />}
      >
        {/* Three selects with one option each - a statement, not a choice. */}
        <StatedRow
          label="Franchise"
          value={form.franchise === '' ? tt('Žiadny') : form.franchise}
          testId="franchise"
        />
        <StatedRow
          label={tt('Provízny partner')}
          value={form.commissionPartner === '' ? tt('Žiadny') : form.commissionPartner}
          testId="commissionPartner"
        />
        <StatedRow
          label={tt('Franchise spôsob fakturácie')}
          value={methodLabel(form.franchiseBillingMethod)}
          testId="franchiseBillingMethod"
        />
        <SettingRow label={tt('Spôsob fakturácie PizzaMajstro')} desc="">
          <select
            className={FIELD}
            aria-label={tt('Spôsob fakturácie PizzaMajstro')}
            data-method-select
            disabled={!writable}
            value={form.fastkitchenBillingMethod}
            onChange={(event) => set('fastkitchenBillingMethod', event.target.value as BillingMethod)}
          >
            {(['percent_of_orders', 'fixed_amount', 'none'] as const).map((method) => (
              <option key={method} value={method}>
                {methodLabel(method)}
              </option>
            ))}
          </select>
        </SettingRow>
        <NumberRow
          field="licenceFeePercent"
          label={tt('Výška poplatku pre franchise v percentách')}
          desc={tt('Percento z tržieb odvádzané centrále - ako poznámka.')}
          suffix="%"
          form={form}
          settings={settings}
          disabled={!writable}
          onChange={set}
        />
      </Section>

      <Section
        title={tt('Poplatky za zariadenia')}
        icon={<CreditCard className="w-4 h-4 text-primary" />}
      >
        <NumberRow
          field="firstDeviceFee"
          label={tt('Poplatok za prvé zariadenie')}
          desc={tt('Hardvér centrála fakturuje po položkách, nie z tohto poľa.')}
          suffix="€"
          form={form}
          settings={settings}
          disabled={!writable}
          onChange={set}
        />
        <NumberRow
          field="additionalDeviceFee"
          label={tt('Poplatok za ďalšie zariadenie')}
          suffix="€"
          form={form}
          settings={settings}
          disabled={!writable}
          onChange={set}
        />
        <NumberRow
          field="cardpayCommission"
          label={tt('Provízia CARDPAY pre PizzaMajstro')}
          suffix="€"
          form={form}
          settings={settings}
          disabled={!writable}
          onChange={set}
        />
      </Section>

      <Section title={tt('Centrálne riadenie')} icon={<BarChart3 className="w-4 h-4 text-primary" />}>
        <Toggle
          label={tt('Centrálne riadenie menu')}
          desc={tt('Poznámka. Vzdialenú úpravu jedálneho lístka toto pole nezapína.')}
          value={form.centralMenuControl}
          disabled={!writable}
          onToggle={() => set('centralMenuControl', !form.centralMenuControl)}
          testId="centralMenuControl"
        />
        <Toggle
          label={tt('Zdieľané reporty s centrálou')}
          desc={tt('Poznámka. Prístup centrály k reportom sa riadi inde.')}
          value={form.sharedReports}
          disabled={!writable}
          onToggle={() => set('sharedReports', !form.sharedReports)}
          testId="sharedReports"
        />
        <TextRow
          field="centralDashboardUrl"
          label={tt('URL centrálneho dashboardu')}
          desc={tt('Odkaz na dashboard franšízora')}
          placeholder="https://saas.pizzamajstro.sk"
          wide
          form={form}
          settings={settings}
          disabled={!writable}
          onChange={set}
        />
      </Section>

      <Section title={tt('Kontakt')} icon={<Phone className="w-4 h-4 text-primary" />}>
        <TextRow
          field="franchiserContact"
          label={tt('Kontakt na franšízora')}
          desc={tt('E-mail alebo telefón na franšízora')}
          placeholder="pizzamajstro@gmail.com"
          wide
          form={form}
          settings={settings}
          disabled={!writable}
          onChange={set}
        />
      </Section>

      <Section title={tt('Synchronizácia')} icon={<RefreshCw className="w-4 h-4 text-primary" />}>
        <Toggle
          label={tt('Automatická synchronizácia nastavení')}
          desc={tt('Poznámka. Žiadna úloha túto sekciu nesynchronizuje.')}
          value={form.autoSync}
          disabled={!writable}
          onToggle={() => set('autoSync', !form.autoSync)}
          testId="autoSync"
        />
      </Section>

      <div className="flex items-center gap-3">
        <button
          type="button"
          data-save
          disabled={!writable || save.isPending || problems.length > 0}
          onClick={() => save.mutate({ data: { ...payloadOf(form) } })}
          className={cn('flex items-center gap-2 px-5 py-2.5 rounded-xl', BUTTON_PRIMARY, 'text-sm')}
        >
          {save.isPending ? <Spinner /> : <Save className="w-4 h-4" />} <T>Uložiť nastavenia</T>
        </button>
        {problems.length > 0 && (
          <span className="text-[11px] text-red-500" data-save-blocked>
            {formatCount(problems.length)} <T>z čísel nie je číslo - opravte ich pred uložením.</T>
          </span>
        )}
      </div>
    </div>
  )
}

function SettingRow({
  label,
  desc,
  children,
}: {
  label: string
  desc: string
  children: React.ReactNode
}) {
  return (
    <div className="flex items-start justify-between px-5 py-4 gap-4">
      <div className="flex-1 pt-1 min-w-0">
        <p className="font-semibold text-foreground text-sm">{label}</p>
        {desc !== '' && <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>}
      </div>
      <div className="shrink-0 space-y-1">{children}</div>
    </div>
  )
}

/** An unstored field is empty, and the legacy default is offered rather than typed in for you. */
function Unset({ field, disabled, onAccept }: { field: string; disabled: boolean; onAccept: () => void }) {
  const suggestion = suggestionFor(field)
  return (
    <p className="text-[11px] text-muted-foreground flex items-center gap-1" data-unset={field}>
      <Info className="w-3 h-3 shrink-0" /> <T>nenastavené</T>
      {suggestion !== null && (
        <>
          {' · '}
          <button
            type="button"
            data-accept-default={field}
            disabled={disabled}
            onClick={onAccept}
            className="text-primary underline disabled:opacity-50"
          >
            <T>doplniť</T> {suggestionLabel(field)}
          </button>
        </>
      )}
    </p>
  )
}

function TextRow<K extends 'appName' | 'franchiseId' | 'franchiseName' | 'centralDashboardUrl' | 'franchiserContact'>({
  field,
  label,
  desc,
  placeholder,
  wide,
  form,
  settings,
  disabled,
  onChange,
}: {
  field: K
  label: string
  desc: string
  placeholder?: string
  wide?: boolean
  form: FranchiseForm
  settings: FranchiseSettings | undefined
  disabled: boolean
  onChange: (key: K, value: string) => void
}) {
  return (
    <SettingRow label={label} desc={desc}>
      <input
        className={cn(FIELD, wide === true && 'w-72')}
        aria-label={label}
        disabled={disabled}
        placeholder={placeholder}
        value={form[field]}
        onChange={(event) => onChange(field, event.target.value)}
      />
      {fieldState(field, form, settings) === 'unset' && (
        <Unset
          field={field}
          disabled={disabled}
          onAccept={() => onChange(field, suggestionFor(field) ?? '')}
        />
      )}
    </SettingRow>
  )
}

function NumberRow({
  field,
  label,
  desc,
  suffix,
  form,
  settings,
  disabled,
  onChange,
}: {
  field: NumericField
  label: string
  desc?: string
  suffix?: string
  form: FranchiseForm
  settings: FranchiseSettings | undefined
  disabled: boolean
  onChange: (key: NumericField, value: string) => void
}) {
  const problem = numberProblem(field, form[field])
  return (
    <SettingRow label={label} desc={desc ?? ''}>
      <div className="flex items-center gap-2">
        <input
          className={cn(FIELD, problem !== null && 'border-red-500')}
          aria-label={label}
          disabled={disabled}
          // Text, not `number`: an empty field must stay empty rather than becoming a 0 on blur, and
          // a Slovak decimal comma has to survive being typed. §14.7 keeps money in cents.
          inputMode={MONEY_FIELDS.includes(field) ? 'decimal' : 'text'}
          value={form[field]}
          onChange={(event) => onChange(field, event.target.value)}
        />
        {suffix !== undefined && (
          <span className="text-sm text-muted-foreground font-medium">{suffix}</span>
        )}
      </div>
      {problem !== null && (
        <p className="text-[11px] text-red-500" data-number-problem={field}>
          {numberProblemMessage(problem)}
        </p>
      )}
      {problem === null && fieldState(field, form, settings) === 'unset' && (
        <Unset
          field={field}
          disabled={disabled}
          onAccept={() => onChange(field, suggestionFor(field) ?? '')}
        />
      )}
    </SettingRow>
  )
}

/** A value with exactly one possibility. Said, not offered. */
function StatedRow({ label, value, testId }: { label: string; value: string; testId: string }) {
  return (
    <SettingRow label={label} desc={tt('Jediná možnosť - mení sa v centrále.')}>
      <p className="text-sm text-muted-foreground" data-stated={testId}>
        {value}
      </p>
    </SettingRow>
  )
}

function Section({
  title,
  icon,
  children,
}: {
  title: string
  icon: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <div className="bg-card border border-border rounded-2xl overflow-hidden">
      <div className="px-5 py-4 border-b border-border flex items-center gap-2">
        {icon}
        <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">{title}</h2>
      </div>
      <div className="divide-y divide-border">{children}</div>
    </div>
  )
}

function Toggle({
  label,
  desc,
  value,
  disabled,
  onToggle,
  testId,
}: {
  label: string
  desc: string
  value: boolean
  disabled: boolean
  onToggle: () => void
  testId: string
}) {
  return (
    <div className="flex items-start justify-between px-5 py-4 gap-4" data-toggle={testId}>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-foreground text-sm">{label}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={value}
        aria-label={label}
        disabled={disabled}
        data-toggle-state={value ? 'on' : 'off'}
        onClick={onToggle}
        className={cn(
          'w-11 h-6 rounded-full relative shrink-0 transition-colors disabled:opacity-50',
          value ? 'bg-primary' : 'bg-muted',
        )}
      >
        <span
          className={cn(
            'absolute top-0.5 w-5 h-5 rounded-full bg-background transition-all',
            value ? 'left-[22px]' : 'left-0.5',
          )}
        />
      </button>
    </div>
  )
}
